import React, { useState, useRef, useEffect } from 'react';
import { snapToNearest } from '../utils/snapMagnitude';
import { formatRelativeTime } from '../utils/formatTime';
import EarthquakeDropdown from './EarthquakeDropdown';
import StepRibbon from './StepRibbon';

function isCoordValid(str) {
  return /^-?\d*\.?\d{0,4}$/.test(str);
}

function makeCoordKeyHandler(onEnter) {
  return e => {
    if (e.key === 'Enter') { onEnter(); return; }
    const { key, target: { value, selectionStart, selectionEnd } } = e;
    if (['Backspace', 'Delete', 'Tab', 'Escape',
         'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
         'Home', 'End'].includes(key)) return;
    if (e.ctrlKey || e.metaKey) return;
    const simulated = value.slice(0, selectionStart) + key + value.slice(selectionEnd);
    if (isCoordValid(simulated)) return;
    e.preventDefault();
  };
}

export default function SearchPanel({ magnitude, onMagnitudeSelect, searchCoords, onSearch }) {
  const [magInput, setMagInput] = useState(magnitude != null ? magnitude.toFixed(1) : '');
  const [magWarning, setMagWarning] = useState(null);
  const magWarnTimer = useRef(null);

  const [inputLat, setInputLat] = useState('');
  const [inputLng, setInputLng] = useState('');
  const [latError, setLatError] = useState(null);
  const [lngError, setLngError] = useState(null);

  const [noMagAlert, setNoMagAlert] = useState(false);
  const noMagTimer = useRef(null);
  const magnitudeRef = useRef(magnitude);
  magnitudeRef.current = magnitude;

  // Earthquake dropdown state
  const [eqOpen, setEqOpen] = useState(false);
  const [eqKey, setEqKey] = useState(0);
  const eqBtnRef = useRef(null);

  // Fill inputs from map click; show alert if Mw is empty
  useEffect(() => {
    if (!searchCoords) return;
    setInputLat(searchCoords.lat.toFixed(4));
    setInputLng(searchCoords.lng.toFixed(4));
    setLatError(null);
    setLngError(null);
    if (!magnitudeRef.current) {
      setNoMagAlert(true);
      if (noMagTimer.current) clearTimeout(noMagTimer.current);
      noMagTimer.current = setTimeout(() => setNoMagAlert(false), 4000);
    } else {
      setNoMagAlert(false);
    }
  }, [searchCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleMagKeyDown(e) {
    const { key, target: { value, selectionStart, selectionEnd } } = e;
    if (key === 'Enter') { const snapped = commitMag(value); handleSearch(snapped); return; }
    if (['Backspace', 'Delete', 'Tab', 'Escape',
         'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
         'Home', 'End'].includes(key)) return;
    if (e.ctrlKey || e.metaKey) return;
    const simulated = value.slice(0, selectionStart) + key + value.slice(selectionEnd);
    if (/^\d*\.?\d?$/.test(simulated)) return;
    e.preventDefault();
  }

  function commitMag(raw) {
    const num = parseFloat(raw);
    if (!raw.trim() || isNaN(num)) return null;
    const snapped = snapToNearest(num);
    setMagInput(snapped.toFixed(1));
    setNoMagAlert(false);
    if (Math.abs(num - snapped) > 1e-9) {
      if (magWarnTimer.current) clearTimeout(magWarnTimer.current);
      setMagWarning(`M ${num.toFixed(1)} not available — snapped to M ${snapped.toFixed(1)}`);
      magWarnTimer.current = setTimeout(() => setMagWarning(null), 10000);
    } else {
      setMagWarning(null);
    }
    onMagnitudeSelect(snapped);
    return snapped;
  }

  function handleSearch(forceMag) {
    // Use forceMag if provided (e.g. just committed from mag field Enter),
    // otherwise fall back to committed magnitude prop, or try to commit magInput now.
    let effectiveMag = forceMag ?? magnitude;
    if (!effectiveMag && magInput.trim()) {
      effectiveMag = commitMag(magInput);
    }
    if (!effectiveMag) return;

    let ok = true;
    const lat = parseFloat(inputLat);
    const lng = parseFloat(inputLng);

    if (!inputLat.trim() || isNaN(lat)) {
      setLatError('Latitude must be between -90 and 90'); ok = false;
    } else if (lat < -90 || lat > 90) {
      setLatError('Latitude must be between -90 and 90'); ok = false;
    } else {
      setLatError(null);
    }

    if (!inputLng.trim() || isNaN(lng)) {
      setLngError('Longitude must be between -180 and 180'); ok = false;
    } else if (lng < -180 || lng > 180) {
      setLngError('Longitude must be between -180 and 180'); ok = false;
    } else {
      setLngError(null);
    }

    if (!ok) return;
    onSearch(lat, lng, { magnitude: effectiveMag });
  }

  // "Use" button in the earthquake dropdown
  function handleUseEarthquake(eq) {
    const snapped = snapToNearest(eq.magnitude);

    // Fill pill bar inputs immediately
    setMagInput(snapped.toFixed(1));
    setInputLat(eq.lat.toFixed(4));
    setInputLng(eq.lon.toFixed(4));
    setLatError(null);
    setLngError(null);

    // Show below-pill snap warning if the magnitude was adjusted
    if (Math.abs(eq.magnitude - snapped) > 1e-9) {
      if (magWarnTimer.current) clearTimeout(magWarnTimer.current);
      setMagWarning(`M ${eq.magnitude.toFixed(1)} not available — snapped to M ${snapped.toFixed(1)}`);
      magWarnTimer.current = setTimeout(() => setMagWarning(null), 4000);
    }

    // Build a richer pin popup for the earthquake event
    const popup =
      `<b>M ${eq.magnitude.toFixed(1)}</b> · ${eq.place}` +
      `<br><span style="color:#888;font-size:11px">${formatRelativeTime(eq.time)}</span>`;

    // Pass snapped magnitude in options so handlePanelSearch can use it even
    // if the App-level magnitude state hasn't updated yet (async batching).
    onSearch(eq.lat, eq.lon, { magnitude: snapped, popup, place: eq.place });
  }

  function handleEqButtonClick() {
    if (!eqOpen) {
      setEqOpen(true);
      // EarthquakeDropdown will fetch on mount; eqKey unchanged.
    } else {
      // Already open: re-mount the dropdown to force a fresh fetch.
      setEqKey(k => k + 1);
    }
  }

  const canSearch    = magnitude !== null && inputLat.trim() !== '' && inputLng.trim() !== '';
  const coordsFilled = inputLat.trim() !== '' && inputLng.trim() !== '';
  const showGuidance = magnitude !== null && !coordsFilled && !noMagAlert;
  const coordKeyDown = makeCoordKeyHandler(handleSearch);

  return (
    <div className="pill-wrapper">
      <div className="pill-bar">
        {/* Magnitude */}
        <div className={`pill-field${noMagAlert ? ' mag-alert' : ''}`}>
          <span className="pill-label">Magnitude</span>
          <input
            className="pill-input mw-input"
            type="text"
            inputMode="decimal"
            placeholder="e.g. 7.8–9.4"
            value={magInput}
            onChange={e => { const v = e.target.value; if (/^\d*\.?\d?$/.test(v)) setMagInput(v); }}
            onKeyDown={handleMagKeyDown}
            onBlur={e => commitMag(e.target.value)}
          />
        </div>

        <div className="pill-divider" />

        {/* Latitude */}
        <div className="pill-field">
          <span className="pill-label">Latitude</span>
          <input
            className={`pill-input coord-input${latError ? ' field-error' : ''}`}
            type="text"
            inputMode="decimal"
            placeholder="e.g. 13.5000"
            title={latError || ''}
            value={inputLat}
            onChange={e => { const v = e.target.value; if (isCoordValid(v)) setInputLat(v); }}
            onKeyDown={coordKeyDown}
          />
        </div>

        <div className="pill-divider" />

        {/* Longitude */}
        <div className="pill-field">
          <span className="pill-label">Longitude</span>
          <input
            className={`pill-input coord-input${lngError ? ' field-error' : ''}`}
            type="text"
            inputMode="decimal"
            placeholder="e.g. 100.5000"
            title={lngError || ''}
            value={inputLng}
            onChange={e => { const v = e.target.value; if (isCoordValid(v)) setInputLng(v); }}
            onKeyDown={coordKeyDown}
          />
        </div>

        <div className="pill-divider" />

        {/* Recent Earthquakes toggle button */}
        <button
          ref={eqBtnRef}
          className={`pill-eq-btn${eqOpen ? ' active' : ''}`}
          onClick={handleEqButtonClick}
          title={eqOpen ? 'Click to refresh' : 'Show recent earthquakes near this region'}
        >
          <span className="eq-pulse-dot" />
          Recent earthquakes
        </button>

        {/* Coordinate search button */}
        <button
          className="pill-search-btn"
          onClick={handleSearch}
          disabled={!canSearch}
          title={!canSearch ? 'Enter magnitude and coordinates first' : 'Find nearest scenarios'}
        >
          🔍
        </button>
      </div>

      <StepRibbon />

      {/* Below-pill messages */}
      {showGuidance && (
        <p className="pill-hint">
          Click on the map or enter coordinates to search for nearby scenarios
        </p>
      )}
      {noMagAlert && (
        <p className="pill-alert">Please enter a magnitude first</p>
      )}
      {magWarning && (
        <p className="pill-snap-warning">{magWarning}</p>
      )}

      {/* Earthquake dropdown (re-mounts on eqKey change → forces re-fetch) */}
      {eqOpen && (
        <EarthquakeDropdown
          key={eqKey}
          btnRef={eqBtnRef}
          onClose={() => setEqOpen(false)}
          onUse={handleUseEarthquake}
        />
      )}
    </div>
  );
}
